import { FC, ReactNode, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Search } from '@mui/icons-material'
import { Box, Button, Card, CardActions, CardContent, Chip, Grid, InputAdornment, Skeleton, Stack, TextField, Typography } from '@mui/material'

import { Game } from '../../../features/game/types/Game'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetInternalFeatureMapQuery } from '../../api/features'
import {
  useGetBulkAssignChoicesQuery,
  useGetBulkAssignFeatureScreenshotsQuery,
  useGetBulkAssignGamesQuery,
  useGetBulkAssignGameScreenshotsQuery,
} from '../../api/implementation-bulk-assign'
import { useGetInternalKeywordsQuery } from '../../api/keywords'
import { InternalFeatureMapFeature, useGetInternalFeatureTagsQuery } from '../../api/screenshot-links'
import { useGetInternalConceptsQuery, useGetInternalTagsQuery } from '../../api/tags'
import { InternalGameSelectorDialog } from '../../components/InternalGameSelectorDialog/InternalGameSelectorDialog'
import { toConceptsById } from '../../features/analysis-screenshots/helpers/screenshotHelpers'
import { BulkAssignGamesFilter } from '../../features/implementation-bulk-assign/components/BulkAssignGamesFilter/BulkAssignGamesFilter'
import { BulkAssignPickerDialog } from '../../features/implementation-bulk-assign/components/BulkAssignPickerDialog/BulkAssignPickerDialog'
import { BulkAssignScreenshotGrid } from '../../features/implementation-bulk-assign/components/BulkAssignScreenshotGrid/BulkAssignScreenshotGrid'
import { saveBulkAssignEdit } from '../../features/implementation-bulk-assign/helpers/bulkAssignEditSelection'
import {
  bulkAssignMarketIso,
  describePickedChoices,
  filterByFeatureLegacyIds,
  filterByGames,
  getScreenshotGameIds,
  sortBulkAssignScreenshots,
} from '../../features/implementation-bulk-assign/helpers/bulkAssignHelpers'
import {
  getBulkAssignEditPath,
  getBulkAssignFeaturesPath,
  getBulkAssignGamesPath,
  parseBulkAssignSelection,
} from '../../features/implementation-bulk-assign/helpers/bulkAssignPaths'
import {
  BulkAssignEditState,
  BulkAssignListState,
  BulkAssignMode,
  BulkAssignSelection,
} from '../../features/implementation-bulk-assign/types/ImplementationBulkAssign'
import { parseScreenshotIds } from '../../features/implementation-examples/helpers/implementationExamplePaths'
import { getFeatureSearchLegacyIds } from '../../helpers/screenshotLinks'
import { InternalTagType } from '../../types/InternalTag'

const maxPickedGames = 20

const StartCard: FC<{ title: string; action: string; onAction: () => void; children?: ReactNode }> = ({ title, action, onAction, children }) => (
  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <CardContent sx={{ flexGrow: 1 }}>
      <Typography variant="h6" mb={1}>
        {title}
      </Typography>
      {children}
    </CardContent>
    <CardActions>
      <Button variant="contained" onClick={onAction}>
        {action}
      </Button>
    </CardActions>
  </Card>
)

const FeaturesSummary: FC<{ selection: BulkAssignSelection }> = ({ selection }) => {
  const { t } = useTranslation()
  const hasChoices = selection.choiceIds.length > 0
  const hasConcepts = selection.conceptIds.length > 0
  const choicesQuery = useGetBulkAssignChoicesQuery(undefined, { skip: !hasChoices })
  const featureMapQuery = useGetInternalFeatureMapQuery({ marketIso: bulkAssignMarketIso }, { skip: !hasChoices })
  const conceptsQuery = useGetInternalConceptsQuery(undefined, { skip: !hasConcepts })
  const tagsQuery = useGetInternalTagsQuery(undefined, { skip: !hasConcepts })
  const features = useMemo(
    () => describePickedChoices(selection.choiceIds, choicesQuery.data ?? [], featureMapQuery.data),
    [selection.choiceIds, choicesQuery.data, featureMapQuery.data]
  )
  const conceptsById = useMemo(
    () =>
      toConceptsById(
        conceptsQuery.data ?? [],
        (tagsQuery.data ?? []).filter((tag) => tag.type === InternalTagType.Concept)
      ),
    [conceptsQuery.data, tagsQuery.data]
  )
  if (!hasChoices && !hasConcepts) return <Typography color="text.secondary">{t('internal-analysts:ie_bulk_assign_features_help')}</Typography>
  if (choicesQuery.isLoading || featureMapQuery.isLoading || conceptsQuery.isLoading || tagsQuery.isLoading) return <Skeleton width="60%" />
  return (
    <Stack spacing={1} aria-label={t('internal-analysts:ie_bulk_assign_picked')}>
      {hasChoices && (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
          {features.map(({ legacyId, name, choices }) => (
            <Chip key={legacyId} size="small" label={name ? `${name}: ${choices.join(', ')}` : choices.join(', ')} />
          ))}
        </Stack>
      )}
      {hasConcepts && (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
          {selection.conceptIds.map((id) => (
            <Chip
              key={id}
              size="small"
              variant="outlined"
              label={conceptsById.get(id)?.name ?? t('internal-analysts:analysis_screenshots_unknown_concept', { id })}
            />
          ))}
        </Stack>
      )}
      {hasChoices && hasConcepts && (
        <Typography variant="body2" color="text.secondary">
          {t('internal-analysts:ie_bulk_assign_choices_and_concepts')}
        </Typography>
      )}
    </Stack>
  )
}

type ResultsProps = { mode: BulkAssignMode; selection: BulkAssignSelection; gameIds: string[] }

const BulkAssignResults: FC<ResultsProps> = ({ mode, selection, gameIds }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [restored] = useState(() => (location.state ?? {}) as BulkAssignListState)
  const featureShots = useGetBulkAssignFeatureScreenshotsQuery(selection, { skip: mode !== 'features', refetchOnMountOrArgChange: true })
  const gameShots = useGetBulkAssignGameScreenshotsQuery(gameIds, { skip: mode !== 'games', refetchOnMountOrArgChange: true })
  const screenshotsQuery = mode === 'features' ? featureShots : gameShots
  const sorted = useMemo(() => sortBulkAssignScreenshots(screenshotsQuery.data ?? []), [screenshotsQuery.data])
  const resultGameIds = useMemo(() => (mode === 'games' ? gameIds : getScreenshotGameIds(sorted)), [mode, gameIds, sorted])
  const gamesQuery = useGetBulkAssignGamesQuery(resultGameIds, { skip: !resultGameIds.length })
  const games = useMemo(() => (gamesQuery.data ?? []).map((game) => new Game(game)), [gamesQuery.data])
  const gamesById = useMemo(() => new Map(games.map((game) => [game.id, game])), [games])
  const featureMapQuery = useGetInternalFeatureMapQuery({ marketIso: bulkAssignMarketIso }, { skip: mode !== 'games' })
  const keywordsQuery = useGetInternalKeywordsQuery(undefined, { skip: mode !== 'games' })
  const featureTagsQuery = useGetInternalFeatureTagsQuery(undefined, { skip: mode !== 'games' })
  const [selectedGameIds, setSelectedGameIds] = useState<string[]>(restored.selectedGameIds ?? (mode === 'games' ? gameIds : []))
  const [selectedScreenshotIds, setSelectedScreenshotIds] = useState<string[]>(restored.selectedScreenshotIds ?? [])
  const [featureSearch, setFeatureSearch] = useState(restored.featureSearch ?? '')
  const deferredSearch = useDeferredValue(featureSearch)
  const scrollRestored = useRef(!restored.scrollY)

  const searchLegacyIds = useMemo(
    () =>
      mode === 'games'
        ? getFeatureSearchLegacyIds(
            deferredSearch,
            Object.values(featureMapQuery.data ?? {}) as InternalFeatureMapFeature[],
            keywordsQuery.data ?? [],
            featureTagsQuery.data ?? []
          )
        : null,
    [mode, deferredSearch, featureMapQuery.data, keywordsQuery.data, featureTagsQuery.data]
  )
  const shown = useMemo(() => filterByFeatureLegacyIds(filterByGames(sorted, selectedGameIds), searchLegacyIds), [sorted, selectedGameIds, searchLegacyIds])
  const isLoading = screenshotsQuery.isLoading || gamesQuery.isLoading

  useEffect(() => {
    if (scrollRestored.current || isLoading || !shown.length) return
    scrollRestored.current = true
    const frame = window.requestAnimationFrame(() => window.scrollTo(0, restored.scrollY ?? 0))
    return () => window.cancelAnimationFrame(frame)
  }, [isLoading, shown.length, restored.scrollY])

  const openEdit = (screenshotIds: string[]) => {
    const listState: BulkAssignListState = { selectedGameIds, selectedScreenshotIds, featureSearch, scrollY: window.scrollY }
    const returnTo = `${location.pathname}${location.search}`
    navigate(returnTo, { replace: true, state: listState })
    const editState: BulkAssignEditState = { screenshotIds, fromList: true }
    navigate(getBulkAssignEditPath(saveBulkAssignEdit({ screenshotIds, returnTo, listState })), { state: editState })
  }

  if (screenshotsQuery.isError) return <Typography color="error.main">{t('internal-analysts:implementation_screenshots_load_failed')}</Typography>

  return (
    <Box mt={3}>
      <Typography variant="h6">
        {t(mode === 'features' ? 'internal-analysts:ie_bulk_assign_games_with_examples' : 'internal-analysts:ie_bulk_assign_picked_games')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        {t('internal-analysts:ie_bulk_assign_games_help', { count: resultGameIds.length, screenshots: sorted.length })}
      </Typography>
      {gamesQuery.isError && <Typography color="error.main">{t('internal-analysts:implementation_games_load_failed')}</Typography>}
      <BulkAssignGamesFilter games={games} selectedGameIds={selectedGameIds} loading={isLoading} onChange={setSelectedGameIds} />
      {mode === 'games' && (
        <TextField
          size="small"
          fullWidth
          sx={{ my: 2 }}
          value={featureSearch}
          onChange={(event) => setFeatureSearch(event.target.value)}
          placeholder={t('internal-analysts:ie_bulk_assign_feature_search')}
          inputProps={{ 'aria-label': t('internal-analysts:ie_bulk_assign_feature_search') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}
      <Box mt={2}>
        <BulkAssignScreenshotGrid
          screenshots={shown}
          gamesById={gamesById}
          selectedIds={selectedScreenshotIds}
          isLoading={isLoading}
          noItemsLabel={t(
            !sorted.length
              ? 'internal-analysts:ie_bulk_assign_no_screenshots'
              : selectedGameIds.length
              ? 'internal-analysts:analysis_screenshots_none_match'
              : 'internal-analysts:ie_bulk_assign_choose_games'
          )}
          onSelectedIdsChange={setSelectedScreenshotIds}
          onEdit={openEdit}
        />
      </Box>
    </Box>
  )
}

export const ImplementationBulkAssignPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-implementation-bulk-assign'))
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { mode } = useParams<{ mode?: string }>()
  const [searchParams] = useSearchParams()
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [isGamesOpen, setIsGamesOpen] = useState(false)
  const selection = useMemo(() => (mode === 'features' ? parseBulkAssignSelection(searchParams) : { choiceIds: [], conceptIds: [] }), [mode, searchParams])
  const gameIds = useMemo(() => (mode === 'games' ? parseScreenshotIds(searchParams.get('gameIds')).slice(0, maxPickedGames) : []), [mode, searchParams])
  const activeMode: BulkAssignMode | undefined =
    mode === 'features' && (selection.choiceIds.length || selection.conceptIds.length) ? 'features' : mode === 'games' && gameIds.length ? 'games' : undefined
  const pickedGamesQuery = useGetBulkAssignGamesQuery(gameIds, { skip: !gameIds.length })
  const pickedGames = useMemo(() => (pickedGamesQuery.data ?? []).map((game) => new Game(game)), [pickedGamesQuery.data])

  return (
    <Box pb={8}>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        {t('internal-analysts:ie_bulk_assign_title')}
      </Typography>
      {mode && !['features', 'games'].includes(mode) && (
        <Typography color="error.main" mb={2}>
          {t('internal-analysts:ie_bulk_assign_unknown_mode')}
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <StartCard
            title={t('internal-analysts:ie_bulk_assign_by_features')}
            action={t('internal-analysts:ie_bulk_assign_select_features')}
            onAction={() => setIsPickerOpen(true)}
          >
            <FeaturesSummary selection={selection} />
          </StartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <StartCard
            title={t('internal-analysts:ie_bulk_assign_by_games')}
            action={t('internal-analysts:ie_bulk_assign_select_games')}
            onAction={() => setIsGamesOpen(true)}
          >
            {gameIds.length ? (
              pickedGamesQuery.isLoading ? (
                <Skeleton width="60%" />
              ) : (
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} aria-label={t('internal-analysts:ie_bulk_assign_picked_games')}>
                  {gameIds.map((id) => (
                    <Chip key={id} size="small" label={pickedGames.find((game) => game.id === id)?.name ?? id} />
                  ))}
                </Stack>
              )
            ) : (
              <Typography color="text.secondary">{t('internal-analysts:ie_bulk_assign_games_pick_help', { count: maxPickedGames })}</Typography>
            )}
          </StartCard>
        </Grid>
      </Grid>

      {activeMode && <BulkAssignResults key={`${activeMode}${location.search}`} mode={activeMode} selection={selection} gameIds={gameIds} />}

      <BulkAssignPickerDialog
        open={isPickerOpen}
        selection={selection}
        onClose={() => setIsPickerOpen(false)}
        onDone={(picked) => {
          setIsPickerOpen(false)
          navigate(getBulkAssignFeaturesPath(picked))
        }}
      />
      <InternalGameSelectorDialog
        open={isGamesOpen}
        title={t('internal-analysts:ie_bulk_assign_select_games')}
        selectedGames={pickedGames}
        maxSelected={maxPickedGames}
        onClose={() => setIsGamesOpen(false)}
        onDone={(games) => {
          setIsGamesOpen(false)
          if (games.length) navigate(getBulkAssignGamesPath(games.map(({ id }) => id)))
        }}
      />
    </Box>
  )
}
